import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

const navItems = [
  ["Home", "/"],
  ["About", "/about"],
  ["Services", "/services"],
  ["Products", "/products"],
  ["Portfolio", "/portfolio"],
  ["Technologies", "/technologies"],
  ["Privacy", "/privacy"],
  ["Terms", "/terms"],
  ["Contact", "/contact"],
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  const active = ({ isActive }) =>
    `relative flex items-center rounded-full px-3 py-2 text-[13px] font-semibold transition-all duration-300 ${
      isActive
        ? "bg-pink-500/10 text-pink-300 shadow-[inset_0_0_20px_rgba(236,72,153,0.06)]"
        : "text-slate-300 hover:bg-white/[0.05] hover:text-white"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full border-b border-white/[0.08] bg-[#070711]/75 backdrop-blur-2xl">
      <div className="pointer-events-none absolute left-1/2 top-0 -z-10 h-px w-[60%] -translate-x-1/2 bg-gradient-to-r from-transparent via-pink-500/60 to-transparent blur-sm" />

      <div className="mx-auto flex w-full max-w-[1500px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="group inline-flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-pink-500/20 blur-xl transition group-hover:bg-pink-500/40" />

            <div className="relative grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.06]">
              <img
                src="/logo.png"
                width={48}
                height={46}
                alt="OjMee Technology Logo"
                className="object-contain"
              />
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-black tracking-tight text-white">
              OjMee Technology
            </h2>

            <p className="mt-0.5 text-[14px] text-pink-300">
              Innovation • Technology • Future
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {navItems.map(([label, path]) => (
            <NavLink key={path} to={path} className={active}>
              {({ isActive }) => (
                <>
                  {label}

                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-gradient-to-r from-pink-400 to-violet-400" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/ai-solutions"
            className="hidden items-center gap-2 rounded-full border border-violet-400/20 bg-violet-400/[0.06] px-4 py-2.5 text-xs font-bold text-violet-200 transition duration-300 hover:border-violet-400/40 hover:bg-violet-400/10 md:flex"
          >
            AI Solutions
          </Link>

          <Link
            to="/contact"
            className="group hidden items-center gap-2 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 px-5 py-2.5 text-xs font-black text-white shadow-lg shadow-pink-500/15 transition duration-300 hover:-translate-y-0.5 hover:shadow-pink-500/30 sm:inline-flex"
          >
            Get Quote
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            className="grid h-11 w-11 place-items-center rounded-2xl border border-white/10 bg-white/[0.05] text-slate-200 transition duration-300 hover:border-pink-400/30 hover:bg-pink-400/10 hover:text-pink-300 xl:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-t border-white/[0.06] bg-[#090713]/95 backdrop-blur-2xl transition-all duration-300 xl:hidden ${
          open
            ? "max-h-[700px] opacity-100"
            : "max-h-0 border-transparent opacity-0"
        }`}
      >
        <div className="mx-auto w-full max-w-[1500px] px-4 pb-5 pt-4 sm:px-6 lg:px-8">
          <nav className="grid gap-1.5">
            {navItems.map(([label, path]) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `group flex items-center justify-between rounded-2xl border px-4 py-3.5 text-sm font-semibold transition-all duration-300 ${
                    isActive
                      ? "border-pink-400/20 bg-gradient-to-r from-pink-500/10 to-violet-500/10 text-pink-300"
                      : "border-transparent bg-white/[0.025] text-slate-300 hover:border-white/10 hover:bg-white/[0.05] hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="flex items-center gap-3">
                      <span
                        className={`h-1.5 w-1.5 rounded-full transition ${
                          isActive
                            ? "bg-pink-400 shadow-[0_0_10px_rgba(244,114,182,0.8)]"
                            : "bg-slate-700"
                        }`}
                      />

                      {label}
                    </span>

                    <ArrowRight
                      className={`h-4 w-4 transition duration-300 ${
                        isActive
                          ? "translate-x-0 text-pink-300"
                          : "-translate-x-1 text-slate-600 group-hover:translate-x-0 group-hover:text-slate-300"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="group mt-4 flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-pink-500 to-violet-600 px-5 py-4 text-sm font-black text-white shadow-lg shadow-pink-500/20"
          >
            Start Your Project
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </header>
  );
}
